/**
 * F-011 (session 3, 2026-05-11) — rollup-status helper.
 *
 * Reconciles tenant.status (business-level: active / suspended / paused)
 * with deployments[].status (per-deployment lifecycle state) so the UI
 * surfaces the worse signal when they diverge.
 *
 * Without this rollup, an operator saw "active" on a tenant whose only
 * deployment had been deprovisioned out from under it. Then clicking
 * Suspend hit F-010 (500 from CP because the deployment can't pause).
 *
 * Rule: if any deployment is in a "downgraded" terminal-or-failed state
 * (deprovisioned / failed / archived), surface THAT as the effective
 * display + the consumer disables lifecycle action buttons. Otherwise
 * show tenant.status verbatim.
 *
 * Extracted from `TenantDetail.tsx` so it can be unit-tested without
 * pulling React in (Adam's vitest config doesn't preload jsx-dev-runtime).
 */

export interface EffectiveTenantStatus {
  displayStatus: string;
  downgraded: boolean;
  reason: string | null;
}

const DOWNGRADED_DEPLOYMENT_STATES = new Set(["deprovisioned", "failed", "archived"]);

export function computeEffectiveTenantStatus(
  tenantStatus: string,
  deployments: Array<{ status: string }>,
): EffectiveTenantStatus {
  const downgraded = deployments.find((d) => DOWNGRADED_DEPLOYMENT_STATES.has(d.status));
  if (downgraded) {
    return {
      displayStatus: downgraded.status,
      downgraded: true,
      reason: `tenant.status='${tenantStatus}' but deployment is '${downgraded.status}'`,
    };
  }
  return { displayStatus: tenantStatus, downgraded: false, reason: null };
}
